import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';

import SearchBar from '../components/SearchBar';
import Layout from '../components/Layout';
import CreateFollowupModal from '../components/Followup/CreateFollowupModal';
import LogFollowupModal from '../components/Followup/LogFollowupModal';
import ViewFollowUpsModal from '../components/Followup/ViewFollowupsModal';
import StartQuotationModal from '../components/Quotation/StartQuotationModal';
import { useNavigate } from 'react-router-dom';
import QueryPickerModal from '../components/Query/QueryPickerModal';
import { fetchQueryById } from '../api/Query';
import { fetchCustomerById } from '../api/Customer';
import { fetchBookingDrafts } from '../api/BookingDraft';
import { Query } from '../types/Query';
import { Customer } from '../types/Customer';

import { Followup, FollowupWithDetails } from '../types/FollowUp';
import { fetchFollowups } from '../api/Followup';

import '../css/Pages.css';
import '../css/Calendar.css';
import '../css/PackagesPage.css';

import * as FiIcons from 'react-icons/fi';
import * as MdIcons from "react-icons/md";
const FaEye = FiIcons.FiEye as unknown as React.FC;
const FaAction = MdIcons.MdOutlinePendingActions as unknown as React.FC;
const FiRefreshCw = FiIcons.FiRefreshCw as unknown as React.FC;
const FiPlus = FiIcons.FiPlus as unknown as React.FC;

type AnyFollowup = Followup | (FollowupWithDetails & Partial<Followup> & any);

const FollowupsPage = () => {
  const navigate = useNavigate();
  const hasFetched = useRef(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [items, setItems] = useState<AnyFollowup[]>([]);
  const [loading, setLoading] = useState(false);
  const [queryCache, setQueryCache] = useState<Record<number, Query>>({});
  const [customerCache, setCustomerCache] = useState<Record<number, Customer>>({});

  const [showShareQuotationModal, setShowShareQuotationModal] = useState(false);
  const [showCreateFollowupModal, setShowCreateFollowupModal] = useState(false);
  const [showViewFollowupsModal, setShowViewFollowupsModal] = useState(false);
  const [showLogFollowupModal, setShowLogFollowupModal] = useState(false);

  const [activeQueryId, setActiveQueryId] = useState<number | null>(null);
  const [activeFollowupId, setActiveFollowupId] = useState<number | null>(null);

  const [currentMonth, setCurrentMonth] = useState(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0,0,0,0);
    return d;
  });

  const loadFollowups = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchFollowups();
      const arr: AnyFollowup[] = Array.isArray(data) ? data : [];
      setItems(arr);
      // Warm caches for upcoming sidebar
      const qids = Array.from(new Set(arr.map((f: any) => f.queryId || f.query_id).filter(Boolean)));
      const missingQ = qids.filter((id) => !queryCache[id]);
      if (missingQ.length > 0) {
        const qList = await Promise.all(missingQ.map((id) => fetchQueryById(id).catch(() => null)));
        const qMap: Record<number, Query> = {} as any;
        qList.forEach((q, i) => { if (q) qMap[missingQ[i]] = q; });
        if (Object.keys(qMap).length > 0) setQueryCache(prev => ({ ...prev, ...qMap }));
        const cids = Array.from(new Set((qList.filter(Boolean) as Query[]).map(q => q.customer?.id).filter((x): x is number => !!x)));
        const missingC = cids.filter(id => !customerCache[id]);
        if (missingC.length > 0) {
          const cList = await Promise.all(missingC.map((id) => fetchCustomerById(id).catch(() => null)));
          const cMap: Record<number, Customer> = {} as any;
          cList.forEach((c, i) => { if (c) cMap[missingC[i]] = c; });
          if (Object.keys(cMap).length > 0) setCustomerCache(prev => ({ ...prev, ...cMap }));
        }
      }
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [queryCache, customerCache]);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    loadFollowups();
  }, [loadFollowups]);

  const openQuotationModal = async (queryId: number) => {
    setActiveQueryId(queryId);
    try {
      const drafts = await fetchBookingDrafts(queryId);
      const list = Array.isArray(drafts) ? drafts : [];
      const filtered = list.filter((d: any) => Number(d.query_id || d.queryId || 0) === Number(queryId));
      if (filtered.length > 0) {
        const sorted = filtered.slice().sort((a: any, b: any) => new Date(b.updated_at || b.created_at || 0).getTime() - new Date(a.updated_at || a.created_at || 0).getTime());
        const d = sorted[0];
        let pkg = (d as any).provider_ref;
        if (typeof pkg === 'string') { try { pkg = JSON.parse(pkg); } catch { /* ignore */ } }
        if (pkg) {
          setShowShareQuotationModal(false);
          navigate('/package/builder', { state: { mode: 'quotation', queryId: queryId, packageFromDraft: pkg } });
          return;
        }
      }
    } catch {
      // fall back to modal
    }
    setShowShareQuotationModal(true);
  };

  const openViewFollowupsModal = (queryId?: number) => {
    setActiveQueryId(queryId || 0);
    setShowViewFollowupsModal(true);
  };

  const openFollowupModal = (queryId: number) => {
    setActiveQueryId(queryId);
    setShowCreateFollowupModal(true);
  };
  const [showQueryPicker, setShowQueryPicker] = useState(false);

  const openLogFollowupModal = (queryId?: number, followupId?: number) => {
    setActiveQueryId(queryId || 0);
    setActiveFollowupId(followupId || 0);
    setShowLogFollowupModal(true);
  };

  const handleOutcomeAction = (outcome: string, queryId: number) => {
    if (outcome === 'followup') {
      openFollowupModal(queryId);
    } else if (outcome === 'quotation') {
      openQuotationModal(queryId);
    }
    setActiveFollowupId(null);
  };

  function localDateKey(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const da = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${da}`;
  }

  function parseFollowupDate(f: AnyFollowup): Date | null {
    const rawDate = (f as any).date || (f as any).followupDate || (f as any).queryDate || '';
    if (!rawDate) return null;
    const timeStrRaw: string = (f as any).time || '';
    const timeStr = timeStrRaw ? (timeStrRaw.length === 5 ? timeStrRaw : timeStrRaw.substring(0,5)) : '00:00';

    // If rawDate already includes time (ISO like 2025-09-24T18:30:00.000Z), use only the date part to avoid timezone shifts for day placement
    const datePart = String(rawDate).includes('T') ? String(rawDate).split('T')[0] : String(rawDate).substring(0, 10);
    // Construct a local datetime to avoid UTC→local day drift
    const composed = `${datePart}T${timeStr}`; // treated as local time by Date
    const d = new Date(composed);
    if (isNaN(d.getTime())) {
      // Fallback to native parse of rawDate
      const d2 = new Date(rawDate);
      return isNaN(d2.getTime()) ? null : d2;
    }
    return d;
  }

  function formatEventTime(f: AnyFollowup, d: Date | null): string {
    const timeStrRaw: string = (f as any).time || '';
    if (timeStrRaw) return timeStrRaw.substring(0,5);
    if (!d) return '';
    return d.toTimeString().slice(0,5);
  }

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return items.filter((f: any) => {
      const qid = f.queryId || f.query_id;
      const qd = qid ? queryCache[qid] : undefined;
      const cid = qd?.customer?.id;
      const cd = cid ? customerCache[cid] : undefined;
      const name = ((f.customerName || cd?.name || '') as string).toLowerCase();
      const desc = ((f.description || qd?.description || '') as string).toLowerCase();
      const type = ((f.queryType || qd?.type || '') as string).toLowerCase();
      return [name, desc, type].some(s => s.includes(q));
    });
  }, [items, searchTerm, queryCache, customerCache]);

  // Build calendar grid for currentMonth
  const calendarDays = useMemo(() => {
    const firstDayOfMonth = new Date(currentMonth);
    const start = new Date(firstDayOfMonth);
    const day = start.getDay(); // 0 Sun
    start.setDate(start.getDate() - day); // start on Sunday
    const cells: Date[] = [];
    for (let i = 0; i < 42; i++) { // 6 weeks
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      cells.push(d);
    }
    return cells;
  }, [currentMonth]);

  const eventsByDate = useMemo(() => {
    const map = new Map<string, AnyFollowup[]>();
    filtered.forEach(f => {
      const d = parseFollowupDate(f);
      if (!d) return;
      const key = localDateKey(d);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(f);
    });
    // sort each day's events by time
    map.forEach(list => list.sort((a, b) => {
      const da = parseFollowupDate(a)?.getTime() || 0;
      const db = parseFollowupDate(b)?.getTime() || 0;
      return da - db;
    }));
    return map;
  }, [filtered]);

  const monthLabel = useMemo(() => {
    return currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' });
  }, [currentMonth]);

  const upcoming = useMemo(() => {
    const now = new Date(); now.setHours(0,0,0,0);
    return filtered
      .map(f => ({ f, d: parseFollowupDate(f) }))
      .filter(x => x.d && x.d >= now)
      .sort((a, b) => (a.d!.getTime() - b.d!.getTime()))
      .slice(0, 20);
  }, [filtered]);

  const todayKey = useMemo(() => localDateKey(new Date()), []);

  const [showDayModal, setShowDayModal] = useState(false);
  const [activeDayKey, setActiveDayKey] = useState<string | null>(null);
  const MAX_EVENTS_PER_DAY = 4;

  const openDayModal = (key: string) => {
    setActiveDayKey(key);
    setShowDayModal(true);
  };

  return (
    <Layout>
      <div className="calendar-page-header">
        <h2>Follow-ups</h2>
        <div className="calendar-controls">
          <button onClick={() => setCurrentMonth(m => { const d = new Date(m); d.setMonth(m.getMonth()-1); return d; })}>
            ‹
          </button>
          <div className="month-label">{monthLabel}</div>
          <button onClick={() => setCurrentMonth(m => { const d = new Date(m); d.setMonth(m.getMonth()+1); return d; })}>
            ›
          </button>
        </div>
        <div style={{ flex: 1 }} />
        <div className="calendar-actions">
          <button className="btn" onClick={() => {
            const d = new Date();
            d.setDate(1);
            d.setHours(0,0,0,0);
            setCurrentMonth(d);
          }} title="Jump to current month">
            Today
          </button>
          <button className="btn" onClick={loadFollowups} disabled={loading} title="Reload follow-ups">
            <FiRefreshCw /> {loading ? 'Loading…' : 'Refresh'}
          </button>
          <button className="btn" onClick={() => {
            setShowQueryPicker(true);
          }} title={'Pick a query to add a follow-up'}>
            <FiPlus /> Add Follow-up
          </button>
        </div>
        <div className="calendar-search">
          <SearchBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            placeholder="Search follow-ups..."
          />
        </div>
      </div>

      <div className="calendar-layout">
        <div className="calendar-grid">
          <div className="calendar-weekdays">
            {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
              <div key={d} className="calendar-weekday">{d}</div>
            ))}
          </div>
          <div className="calendar-cells">
            {calendarDays.map((d, idx) => {
              const key = localDateKey(d);
              const inMonth = d.getMonth() === currentMonth.getMonth();
              const dayEvents = eventsByDate.get(key) || [];
              return (
                <div key={idx} className={`calendar-cell ${inMonth ? '' : 'muted'} ${key === todayKey ? 'today' : ''}`}>
                  <div className="cell-date">{d.getDate()}</div>
                  <div className="cell-events">
                    {dayEvents.slice(0, MAX_EVENTS_PER_DAY).map((f, i) => {
                      const dt = parseFollowupDate(f);
                      const time = formatEventTime(f, dt);
                      const any: any = f;
                      const qid = any.queryId || any.query_id;
                      const qd = qid ? queryCache[qid] : undefined;
                      const loc = qd?.location || '';
                      const name = any.customerName || qd?.customer?.name || 'Customer';
                      const title = [name, loc, time].filter(Boolean).join(' · ');
                      return (
                        <div key={i} className="event-pill" title={title} onClick={() => openViewFollowupsModal(qid)}>
                          <span className="event-time">{time}</span>
                          <span className="event-title">{name}</span>
                          {loc && <span className="event-meta">{loc}</span>}
                        </div>
                      );
                    })}
                    {dayEvents.length > MAX_EVENTS_PER_DAY && (
                      <button className="more-link" onClick={() => openDayModal(key)}>+{dayEvents.length - MAX_EVENTS_PER_DAY} more</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <aside className="calendar-sidebar">
          <div className="sidebar-title">Upcoming</div>
          <div className="upcoming-list">
            {upcoming.map(({ f, d }) => {
              const any: any = f;
              const qid = any.queryId || any.query_id;
              const qd = qid ? queryCache[qid] : undefined;
              const cid = qd?.customer?.id;
              const cd = cid ? customerCache[cid] : undefined;
              const displayName = any.customerName || cd?.name || 'Customer';
              const email = any.customerEmail || cd?.email;
              const phone = any.customerPhoneNumber || cd?.phoneNumber;
              const qType = any.queryType || qd?.type || '';
              const qDesc = any.queryDescription || qd?.description || '';
              return (
                <div key={(any.id) || `${qid}-${any.description}`} className="upcoming-item">
                  <div className="upcoming-when">
                    <div className="date">{d?.toLocaleDateString()}</div>
                    <div className="time">{d ? d.toTimeString().slice(0,5) : ''}</div>
                  </div>
                  <div className="upcoming-info">
                    <div className="name">{displayName}</div>
                    {(!!email || !!phone) && (
                      <div className="contact">
                        {email && (
                          <a href={`mailto:${email}`}>{email}</a>
                        )}
                        {phone && (
                          <a href={`tel:${phone}`}>{phone}</a>
                        )}
                      </div>
                    )}
                    <div className="query">{[qType, qDesc].filter(Boolean).join(' — ')}</div>
                    <div className="actions">
                      <button className="small" onClick={() => openViewFollowupsModal(qid)}><FaEye /> History</button>
                      <button className="small" onClick={() => openLogFollowupModal(qid, any.id)}><FaAction /> Log</button>
                    </div>
                  </div>
                </div>
              );
            })}
            {upcoming.length === 0 && (
              <div className="empty">No upcoming follow-ups</div>
            )}
          </div>
        </aside>
      </div>

      {showShareQuotationModal && activeQueryId && (
        <StartQuotationModal
          queryId={activeQueryId}
          onClose={() => setShowShareQuotationModal(false)}
          onCreateFromScratch={() => {
            setShowShareQuotationModal(false);
            navigate('/package/builder', { state: { mode: 'quotation', queryId: activeQueryId } });
          }}
          onUsePackage={(pkg) => {
            setShowShareQuotationModal(false);
            navigate('/package/builder', { state: { mode: 'quotation', queryId: activeQueryId, packageToCopy: pkg } });
          }}
        />
      )}

      {showCreateFollowupModal && activeQueryId && (
        <CreateFollowupModal
          queryId={activeQueryId}
          onClose={() => setShowCreateFollowupModal(false)}
        />
      )}

      {showViewFollowupsModal && activeQueryId && (
        <ViewFollowUpsModal
          queryId={activeQueryId || 0}
          onClose={() => setShowViewFollowupsModal(false)}
          onLog={(fid) => {
            setShowViewFollowupsModal(false);
            openLogFollowupModal(activeQueryId || 0, fid);
          }}
        />
      )}

      {showLogFollowupModal && activeFollowupId && (
        <LogFollowupModal
          onClose={() => setShowLogFollowupModal(false)}
          onOutcomeAction={handleOutcomeAction}
          queryId={activeQueryId || 0}
          followupId={activeFollowupId || 0}
        />
      )}

      {showQueryPicker && (
        <QueryPickerModal
          onClose={() => setShowQueryPicker(false)}
          onSelect={(q) => {
            setShowQueryPicker(false);
            openFollowupModal(q.id);
          }}
        />
      )}

      {showDayModal && activeDayKey && (
        <div className="modal-backdrop" onClick={() => setShowDayModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ width: 520 }}>
            <button className="close-btn" onClick={() => setShowDayModal(false)}>&times;</button>
            <h2>
              {new Date(activeDayKey).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            </h2>
            <div className="day-modal-list">
              {(eventsByDate.get(activeDayKey) || []).map((f, idx) => {
                const d = parseFollowupDate(f)!;
                const time = d ? d.toTimeString().slice(0,5) : '';
                const any: any = f;
                const qid = any.queryId || any.query_id;
                const qd = qid ? queryCache[qid] : undefined;
                const name = any.customerName || qd?.customer?.name || 'Customer';
                const qtype = any.queryType || '';
                const loc = qd?.location || '';
                return (
                  <div key={idx} className="day-modal-item">
                    <div className="when">{time}</div>
                    <div className="info">
                      <div className="name">{name}</div>
                      <div className="meta">{[qtype, loc].filter(Boolean).join(' • ')}</div>
                      <div className="actions">
                        <button className="small" onClick={() => { setShowDayModal(false); openViewFollowupsModal(qid); }}><FaEye /> History</button>
                        <button className="small" onClick={() => { setShowDayModal(false); openLogFollowupModal(qid, any.id); }}><FaAction /> Log</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowDayModal(false)} className="secondary">Close</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default FollowupsPage;
